import { memo } from 'react';
import { MenuItemDisplayData } from '../../types';
import { formatCurrency } from '../../utils/currency';
import LazyImage from '../ui/LazyImage';

interface MenuItemProps {
  item: MenuItemDisplayData;
  onClick: () => void;
}

/**
 * MenuItem Component
 *
 * Displays individual menu item with:
 * - Name, description, and price
 * - Image (if available)
 * - Customization indicator
 * - Availability status
 *
 * Optimized with React.memo to prevent unnecessary re-renders
 */
const MenuItem: React.FC<MenuItemProps> = memo(({ item, onClick }) => {
  return (
    <button
      onClick={item.isAvailable ? onClick : undefined}
      disabled={!item.isAvailable}
      className={`
        w-full bg-white hover:bg-gray-50 rounded-xl p-2 sm:p-4 transition-all duration-200
        border border-gray-100 hover:border-gray-200
        text-left group relative
        ${!item.isAvailable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:gap-4 items-start">
        {/* Menu Item Details */}
        <div className="flex-1 min-w-0">
          {/* Rating/Badge */}
          {item.name.toLowerCase().includes('highly') && (
            <div className="flex items-center gap-1 mb-2">
              <svg className="w-4 h-4 fill-green-600" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span className="text-xs font-medium text-gray-700">Highly rated</span>
            </div>
          )}

          {/* Item name */}
          <h3 className="font-bold text-gray-900 mb-1 text-xs sm:text-base leading-snug">{item.name}</h3>

          {/* Description */}
          {item.description && (
            <p className="hidden sm:block text-sm text-gray-600 mb-2 line-clamp-2">{item.description}</p>
          )}

          {/* Price */}
          <p className="text-xs sm:text-base font-semibold text-gray-900">{formatCurrency(item.price)}</p>

          {/* Availability status */}
          {!item.isAvailable && (
            <span className="inline-block mt-2 text-xs text-red-600 font-medium">
              Currently unavailable
            </span>
          )}
        </div>

        {/* Menu Item Image */}
        <div className="relative w-full sm:w-auto sm:flex-shrink-0">
          {item.image ? (
            <LazyImage src={item.image} alt={item.name} className="w-full h-24 sm:w-28 sm:h-28 rounded-lg object-cover" />
          ) : (
            <div className="w-full h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
              <span className="text-2xl sm:text-3xl">🍽️</span>
            </div>
          )}

          {/* Add button */}
          {item.isAvailable && (
            <div className="absolute -bottom-2 -right-2">
              <div className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-primary group-hover:bg-primary transition-colors">
                <svg
                  className="w-5 h-5 text-primary group-hover:text-mina-espresso transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>
            </div>
          )}
        </div>
      </div>
    </button>
  );
});

MenuItem.displayName = 'MenuItem';

export default MenuItem;
